import { z } from "zod";

import type { ValueSubtypeRequirement } from "../../valueSubtypes.ts";

/**
 * The id of the locale subtype — a string naming one of the module's content
 * languages. Core owns this one because `locale` is core's own external value;
 * no app or node contributes it, so nothing can be left unwired.
 */
export const LOCALE_SUBTYPE_ID = "locale";

/** A string naming one of the module's content languages. */
export const LocaleSubtype = z.object({
  id: z.literal(LOCALE_SUBTYPE_ID),
});
export type LocaleSubtype = z.infer<typeof LocaleSubtype>;

/**
 * The offer the `locale` external value declares.
 *
 * Parameterless: the language list is a module's content languages, which the
 * Comparison View reads from state directly and so can offer by *title*. Carried
 * as params it could only be codes — and the descriptor holding this instance is
 * a module const with no store to read.
 */
export const localeSubtype = (): LocaleSubtype => ({ id: LOCALE_SUBTYPE_ID });

/** Derived from the schema rather than hand-rolled: `id` is an open string. */
export const isLocaleSubtype = (subtype: unknown): subtype is LocaleSubtype => LocaleSubtype.safeParse(subtype).success;

/**
 * The requirement of a slot holding a locale. It states the id and nothing more:
 * one module's language set is in play per session, so every locale in scope is
 * drawn from the same list and any locale satisfies any locale slot.
 */
export const requireLocale = (): ValueSubtypeRequirement => ({ id: LOCALE_SUBTYPE_ID });
